import React, { useMemo, useRef, useEffect } from "react";
import { Group, Text } from "react-konva";
import Konva from "konva";
import { IMondayTaskController } from "../../controller";
import { MondayItemData } from "shared/integrations/monday-types";
import { FONT_SIZE, FONT_FAMILY, PADDING, AVATAR_SIZE } from "../../consts";
import { formatDistanceToNow } from "date-fns";
import { measureTextWidth } from "../../text-utils";
import { ExpandUpdateButton } from "./expand-update-button";
import { UserAvatar } from "./user-avatar";
import { MONDAY_TASK_UPDATES_ROW_HEIGHT, MONDAY_TASK_WIDTH } from "shared/datamodel/monday-task";

const TIME_COLOR = "#7F869B";
const TEXT_COLOR = "#1E1E1E";

const NAME_X = AVATAR_SIZE + 8;
const MAX_NAME_WIDTH = 125;
const NAME_TIME_GAP = 8;
const UPDATE_TEXT_Y = 18;
const UPDATE_TEXT_WIDTH = MONDAY_TASK_WIDTH - PADDING * 2 - NAME_X - 6;

interface UpdateRowProps {
  controller: IMondayTaskController;
  update: MondayItemData["updates"][0];
  isExpanded: boolean;
  y: number;
}

export const UpdateRow = React.memo(({ controller, update, isExpanded, y }: UpdateRowProps) => {
  const timeAgo = useMemo(() => formatTimeAgo(update.createdAt), [update.createdAt]);

  const nameWidth = useMemo(() => {
    const { width } = measureTextWidth(update.userName, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontStyle: "500",
      maxWidth: MAX_NAME_WIDTH,
    });
    return width;
  }, [update.userName]);

  const isTruncated = useMemo(() => {
    const { isTruncated } = measureTextWidth(update.text, {
      fontSize: FONT_SIZE,
      fontFamily: FONT_FAMILY,
      fontStyle: "400",
      maxWidth: UPDATE_TEXT_WIDTH,
      maxHeight: MONDAY_TASK_UPDATES_ROW_HEIGHT - FONT_SIZE,
    });
    return isTruncated;
  }, [update.text]);

  const timeX = NAME_X + nameWidth + NAME_TIME_GAP;

  // Cache the text group (recreate when content or expansion changes)
  const groupRef = useRef<Konva.Group>(null);
  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.clearCache();
      groupRef.current.cache({ pixelRatio: 4 });
    }
  }, [update.userName, timeAgo, update.text, isExpanded]);

  return (
    <Group y={y}>
      <UserAvatar url={update.userURL} />

      <Group y={4} ref={groupRef}>
        <Text
          x={NAME_X}
          text={update.userName}
          fill={TEXT_COLOR}
          fontFamily={FONT_FAMILY}
          fontSize={FONT_SIZE}
          fontStyle="500"
          letterSpacing={0.1871}
          wrap="none"
          ellipsis
          width={MAX_NAME_WIDTH}
        />

        <Text
          x={timeX}
          y={1}
          text={timeAgo}
          fill={TIME_COLOR}
          fontFamily={FONT_FAMILY}
          fontSize={10}
          letterSpacing={0.1871}
          wrap="none"
        />

        {isExpanded ? (
          // When expanded, show full text wrapped across multiple lines
          <>
            <Text
              x={NAME_X}
              y={UPDATE_TEXT_Y}
              text={update.text}
              fill={TEXT_COLOR}
              fontFamily={FONT_FAMILY}
              fontSize={FONT_SIZE}
              letterSpacing={0.1737}
              wrap="word"
              width={UPDATE_TEXT_WIDTH}
              lineHeight={1.2}
            />
          </>
        ) : (
          // When collapsed, show truncated text with ellipsis
          <>
            <Text
              x={NAME_X}
              y={UPDATE_TEXT_Y}
              text={update.text}
              fill={TEXT_COLOR}
              fontFamily={FONT_FAMILY}
              fontSize={FONT_SIZE}
              letterSpacing={0.1737}
              wrap="none"
              ellipsis
              width={UPDATE_TEXT_WIDTH}
              height={MONDAY_TASK_UPDATES_ROW_HEIGHT - FONT_SIZE}
              lineHeight={1.2}
            />
          </>
        )}
      </Group>

      {(isTruncated || isExpanded) && (
        <Group y={4}>
          <ExpandUpdateButton x={NAME_X + UPDATE_TEXT_WIDTH - PADDING} controller={controller} update={update} />
        </Group>
      )}
    </Group>
  );
});

UpdateRow.displayName = "UpdateRow";

/**
 * Format a date string to relative time (e.g., "2 hours ago", "2 days ago")
 */
function formatTimeAgo(dateString: string): string {
  let result = formatDistanceToNow(new Date(dateString), { addSuffix: true });
  result = result.replace("about ", "");
  return result.trim();
}
